// Founder-confirmed Sender ID authority, 21 September 2026.
// Registration is provider-controlled. Existing configured Verify supports SMS;
// eligibility is not a claim that a new live handset-delivery test has run.
export const OTP_POLICY_VERSION = '2026-09-26-uganda-sender-id';
export const SENDER_COUNTRIES = Object.freeze({
  AU: { country: 'Australia', prefix: '+61', registration: 'active', approved: '12/06/2026' },
  GH: { country: 'Ghana', prefix: '+233', registration: 'active' },
  ZM: { country: 'Zambia', prefix: '+260', registration: 'active' },
  BW: { country: 'Botswana', prefix: '+267', registration: 'registration_not_required' },
  AO: { country: 'Angola', prefix: '+244', registration: 'registration_not_required' },
  CD: { country: 'Democratic Republic of the Congo', prefix: '+243', registration: 'registration_not_required' },
  LS: { country: 'Lesotho', prefix: '+266', registration: 'registration_not_required' },
  MG: { country: 'Madagascar', prefix: '+261', registration: 'registration_not_required' },
  MW: { country: 'Malawi', prefix: '+265', registration: 'registration_not_required' },
  MU: { country: 'Mauritius', prefix: '+230', registration: 'registration_not_required' },
  NA: { country: 'Namibia', prefix: '+264', registration: 'registration_not_required' },
  SC: { country: 'Seychelles', prefix: '+248', registration: 'registration_not_required' },
  TZ: { country: 'Tanzania', prefix: '+255', registration: 'in_review' },
  ZW: { country: 'Zimbabwe', prefix: '+263', registration: 'in_review' },
  // Twilio ticket 27283588 confirms that the case-sensitive Sender ID
  // `AfriMed` is registered for Uganda. Verify remains a separately managed
  // channel; this expresses SMS destination authority only.
  UG: { country: 'Uganda', prefix: '+256', registration: 'active', senderId: 'AfriMed', approved: '25/09/2026' },
  GB: { country: 'United Kingdom', prefix: '+44', registration: 'in_review' },
  FR: { country: 'France', prefix: '+33', registration: 'in_review' },
  MZ: { country: 'Mozambique', prefix: '+258', registration: 'in_review' },
  ZA: { country: 'South Africa', prefix: '+27', registration: 'unsupported' },
  // Twilio supports preregistered alphanumeric senders in Eswatini. AfriMed's
  // account approval is not evidenced; do not confuse this with unsupported.
  SZ: { country: 'Eswatini', prefix: '+268', registration: 'not_yet_classified',
    registrationRequirement: 'required', alphanumericSupported: true,
    evidence: 'https://www.twilio.com/en-us/guidelines/sz/sms' },
  CM: { country: 'Cameroon', prefix: '+237', registration: 'not_yet_classified' },
  KE: { country: 'Kenya', prefix: '+254', registration: 'not_yet_classified' },
  NG: { country: 'Nigeria', prefix: '+234', registration: 'not_yet_classified' },
  RW: { country: 'Rwanda', prefix: '+250', registration: 'not_yet_classified' },
  AT: { country: 'Austria', prefix: '+43', registration: 'not_yet_classified' },
  BE: { country: 'Belgium', prefix: '+32', registration: 'not_yet_classified' },
  CA: { country: 'Canada', prefix: '+1', registration: 'not_yet_classified' },
  DE: { country: 'Germany', prefix: '+49', registration: 'not_yet_classified' },
  NZ: { country: 'New Zealand', prefix: '+64', registration: 'not_yet_classified' },
  CH: { country: 'Switzerland', prefix: '+41', registration: 'not_yet_classified' },
  AE: { country: 'United Arab Emirates', prefix: '+971', registration: 'not_yet_classified' },
  US: { country: 'United States', prefix: '+1', registration: 'not_yet_classified' },
});
export function normalizeVerificationPhone(value) {
  const digits = String(value || '').replace(/[\s().-]+/g, '');
  const phone = digits && !digits.startsWith('+') ? `+${digits}` : digits;
  return /^\+[1-9]\d{7,14}$/.test(phone) ? phone : '';
}
export function resolvePhoneCapabilities(phone, { providerConfigured = false, smsEnabled = true, whatsappEnabled = true } = {}) {
  const normalized = normalizeVerificationPhone(phone);
  const matches = Object.entries(SENDER_COUNTRIES).filter(([, row]) => normalized.startsWith(row.prefix));
  const longestPrefix = Math.max(0, ...matches.map(([, row]) => row.prefix.length));
  const candidates = matches.filter(([, row]) => row.prefix.length === longestPrefix);
  // A shared calling code (+1) cannot prove a destination. Keep it unclassified
  // rather than arbitrarily assigning Canada, the US, or an SMS clearance.
  const [country, row] = candidates.length === 1 ? candidates[0] :
    ['UNKNOWN', { country: 'Unclassified destination', registration: 'not_yet_classified' }];
  const eligible = ['active', 'registration_not_required'].includes(row.registration);
  const sms = !!normalized && providerConfigured && eligible && smsEnabled;
  const whatsapp = !!normalized && providerConfigured && whatsappEnabled;
  return { policyVersion: OTP_POLICY_VERSION, country, countryName: row.country,
    senderRegistrationStatus: row.registration,
    classificationRequired: row.registration === 'not_yet_classified',
    countryCandidates: candidates.map(([iso]) => iso),
    senderRegistrationRequirement: row.registrationRequirement || null,
    alphanumericSupported: row.alphanumericSupported ?? null,
    senderOperationalStatus: sms ? 'enabled' : eligible ? 'temporarily_unavailable' : 'unavailable',
    whatsapp, sms, onboardingOtpEnabled: whatsapp || sms, editPhoneOtpEnabled: whatsapp || sms,
    fallbackPermitted: whatsapp && sms, channels: [whatsapp && 'whatsapp', sms && 'sms'].filter(Boolean) };
}
export function resolvePhoneVerificationState({ phone, savedPhone, verifiedAt, authPhone, authConfirmedAt, capabilities, testing = false, pending = false }) {
  const current = normalizeVerificationPhone(phone);
  const saved = normalizeVerificationPhone(savedPhone);
  const verified = !!current && ((current === saved && !!verifiedAt) ||
    (current === normalizeVerificationPhone(authPhone) && !!authConfirmedAt));
  const available = !!capabilities?.channels?.length;
  return { verified, available, saved: !!current && current === saved,
    state: verified ? (testing && available ? 'verification_test' : 'verified') :
      !current || current !== saved ? 'capture' : !available ? 'unavailable' : pending ? 'pending' : 'ready',
    canSend: available && !!current && current === saved && (!verified || testing),
    canTest: verified && available && current === saved };
}
export function phoneSavePatch({ phone, savedPhone, verifiedAt }) {
  return { phone: normalizeVerificationPhone(phone),
    ...(normalizeVerificationPhone(phone) !== normalizeVerificationPhone(savedPhone) ? { phone_verified_at: null } : {}) };
}
